/**
 * Wrapper component for AddUserRoleDialog to work with institute admin management
 * This bridges the interface gap between the generic dialog and the data management config
 */

import React from 'react';
import { AddUserRoleDialog, AddUserRoleDialogConfig } from '@shared/components/user-role';
import { useInviteInstituteAdmin } from '../hooks/useInstituteAdmins';
import { useUserExists } from '@shared/hooks/useUserExists';
interface AddInstituteAdminDialogProps {
  open: boolean;
  onClose: () => void;
  editingItem?: any; // Not used for invite flow
  onSuccess?: () => void;
  tenantId: string;
  domainId: string;
}

export const AddInstituteAdminDialog: React.FC<AddInstituteAdminDialogProps> = ({
  open,
  onClose,
  onSuccess,
  tenantId,
  domainId,
}) => {
  const inviteMutation = useInviteInstituteAdmin(tenantId, domainId);

  const config: AddUserRoleDialogConfig = {
    title: 'Add Institute Administrator',
    description: 'Invite a user to become an administrator for this institute.',
    submitLabel: 'Send Invitation',
    emailLabel: 'Email Address',
    nameLabel: 'Full Name',
    nameRequired: false,
    onSubmit: async (data) => {
      await inviteMutation.mutateAsync({
        email: data.email,
        name: data.name || undefined,
      });
    },
    useUserExistsHook: (email, enabled) => {
      const result = useUserExists(email, { enabled });
      return {
        data: result.data
          ? {
              exists: result.exists ?? false,
              user: result.exists
                ? {
                    name: result.data.user?.name,
                    email: result.data.user?.email || result.data.email,
                  }
                : undefined,
            }
          : undefined,
        isLoading: result.isLoading,
      };
    },
  };

  return (
    <AddUserRoleDialog
      open={open}
      onClose={onClose}
      onSuccess={onSuccess}
      config={config}
      isLoading={inviteMutation.isPending}
    />
  );
};
